/**
 * Route Guard Logic - 路由守衛邏輯
 * 
 * This module contains the pure guard logic that can be tested independently
 * without Vue Router dependencies.
 * 
 * Requirements: 12.7
 */

import type { RouteLocationNormalized } from 'vue-router';

/**
 * Key for storing the intended destination in sessionStorage
 */
export const REDIRECT_KEY = 'kingde_redirect_after_login';

/**
 * Store the intended destination for redirect after login
 * 
 * @param path - The path the user was trying to access
 */
export function storeRedirectPath(path: string): void {
  if (path && path !== '/login') {
    sessionStorage.setItem(REDIRECT_KEY, path);
  }
}

/**
 * Get and clear the stored redirect path
 * 
 * @returns The stored path or null if none exists
 */
export function getAndClearRedirectPath(): string | null {
  const path = sessionStorage.getItem(REDIRECT_KEY);
  sessionStorage.removeItem(REDIRECT_KEY);
  return path;
}

/**
 * Check if a route requires authentication
 * 
 * @param to - The target route
 * @returns true if the route requires authentication
 */
export function requiresAuthentication(to: RouteLocationNormalized): boolean {
  // Check if any matched route requires auth
  return to.matched.some(record => {
    return record.meta.requiresAuth === true;
  });
}

/**
 * Check if a route requires admin permission
 * 
 * @param to - The target route
 * @returns true if the route requires admin permission
 */
export function requiresAdminPermission(to: RouteLocationNormalized): boolean {
  // Check if any matched route requires admin
  return to.matched.some(record => {
    return record.meta.requiresAdmin === true;
  });
}

/**
 * Navigation guard logic
 * 
 * This function contains the core guard logic and can be tested independently.
 * 
 * @param to - Target route
 * @param isAuthenticated - Whether the user is authenticated
 * @param isAdmin - Whether the user has admin permission
 * @returns The redirect path or true to proceed
 * 
 * Requirements: 12.7
 */
export function evaluateNavigationGuard(
  to: RouteLocationNormalized,
  isAuthenticated: boolean,
  isAdmin: boolean
): string | boolean {
  const needsAuth = requiresAuthentication(to);
  const needsAdmin = requiresAdminPermission(to);

  // If route requires auth and user is not authenticated
  if (needsAuth && !isAuthenticated) {
    // Store the intended destination
    storeRedirectPath(to.fullPath);
    return '/login';
  }

  // If route requires admin and user is not admin
  if (needsAdmin && !isAdmin) {
    return '/declarations';
  }

  // If user is authenticated and trying to access login page
  if (to.path === '/login' && isAuthenticated) {
    return '/declarations';
  }

  // Allow navigation
  return true;
}
